import React, { useState, useEffect } from 'react';
import { X, Lock, Mail, AlertCircle, Shield, ShieldCheck, Wifi, KeyRound, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Level2LoginModal from './Level2LoginModal';

const LOGO_KEY = 'https://customer-assets.emergentagent.com/job_2c74cda5-f28e-477a-a538-792d83f7b37f/artifacts/9pieqpxz_ShireAdvisors_Logo-Key.png';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REMEMBERED_EMAIL_KEY = 'shire_remembered_email';

const LoginModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [clientIp, setClientIp] = useState('');
  const [showLevel2, setShowLevel2] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(false);
  const { login } = useAuth();

  // Pre-fill the remembered email (if any) when the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    try {
      const saved = localStorage.getItem(REMEMBERED_EMAIL_KEY);
      if (saved) {
        setEmail(saved);
        setRememberEmail(true);
      }
    } catch (_) { /* localStorage unavailable */ }
  }, [isOpen]);

  // Detect the requesting IP so it can be shown as part of the security
  // signals (helps clients/staff confirm where they're connecting from).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    axios
      .get(`${API}/analytics/whoami`)
      .then((res) => {
        if (!cancelled) setClientIp(res.data?.ip || '');
      })
      .catch(() => {
        if (!cancelled) setClientIp('');
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      // Persist or clear the remembered email based on the toggle
      try {
        if (rememberEmail) {
          localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
        } else {
          localStorage.removeItem(REMEMBERED_EMAIL_KEY);
        }
      } catch (_) { /* ignore */ }
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const isSecureConnection =
    typeof window !== 'undefined' && window.location.protocol === 'https:';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      data-testid="login-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-navy-900 px-6 py-7 text-center relative">
          {/* Secure-connection chip */}
          {isSecureConnection && (
            <div
              className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-[10px] font-mono uppercase tracking-wider"
              data-testid="login-secure-chip"
            >
              <ShieldCheck className="w-3 h-3" />
              Secure
            </div>
          )}

          <button
            type="button"
            onClick={() => setShowLevel2(true)}
            className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-4 p-2 border-2 border-gold-500/30 hover:border-cyan-400 hover:scale-105 transition-all relative group cursor-pointer"
            title="Level 2 Access"
            data-testid="login-modal-level2-trigger"
          >
            <img src={LOGO_KEY} alt="Shire Advisors" className="w-full h-full object-contain" />
            <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-cyan-500 border-2 border-navy-900 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-[9px] font-bold text-navy-900">L2</span>
            </span>
          </button>
          <h2 className="text-2xl font-serif font-bold text-white">Clients &amp; Staff</h2>
          <p className="text-white/70 text-sm mt-2">
            Sign in to access your private workspace
          </p>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
          data-testid="login-modal-close"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 pt-6 pb-2 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-navy-900 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 py-3"
                placeholder="you@example.com"
                required
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-navy-900 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10 py-3"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy-900 transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                title={showPassword ? 'Hide password' : 'Show password'}
                data-testid="login-password-toggle"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-600 select-none cursor-pointer">
            <input
              type="checkbox"
              checked={rememberEmail}
              onChange={(e) => setRememberEmail(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-gold-500 focus:ring-gold-400 cursor-pointer"
              data-testid="login-remember-email-toggle"
            />
            <span>Remember my email on this device</span>
          </label>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold py-3 rounded-lg transition-all"
            data-testid="login-submit-button"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-navy-900/30 border-t-navy-900 rounded-full animate-spin" />
                Signing in...
              </span>
            ) : (
              <span className="inline-flex items-center justify-center gap-2">
                <KeyRound className="w-4 h-4" />
                Sign In
              </span>
            )}
          </Button>
        </form>

        {/* Security footer */}
        <div className="px-6 pb-5 pt-3 border-t border-gray-100 mt-4">
          <div className="grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wider font-mono text-gray-500">
            <div className="flex items-center gap-1.5" title="TLS-encrypted connection">
              <Shield className="w-3.5 h-3.5 text-emerald-600" />
              <span>TLS&nbsp;1.3</span>
            </div>
            <div className="flex items-center gap-1.5" title="Bcrypt-hashed credentials">
              <Lock className="w-3.5 h-3.5 text-emerald-600" />
              <span>Bcrypt</span>
            </div>
            <div className="flex items-center gap-1.5" title="JWT session token">
              <KeyRound className="w-3.5 h-3.5 text-emerald-600" />
              <span>JWT</span>
            </div>
          </div>

          <div
            className="mt-3 flex items-center justify-between text-[11px] text-gray-500 font-mono"
            data-testid="login-ip-row"
          >
            <span className="inline-flex items-center gap-1.5">
              <Wifi className="w-3.5 h-3.5 text-gray-400" />
              Connecting from
            </span>
            <span className="text-navy-900 font-semibold tracking-wide">
              {clientIp || '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Level 2 Modal — triggered by clicking the circular logo */}
      <Level2LoginModal
        isOpen={showLevel2}
        onClose={() => {
          setShowLevel2(false);
          onClose();
        }}
      />
    </div>
  );
};

export default LoginModal;
